import React, { useCallback, useEffect } from 'react';

import { couponVoyageButtonClickAtom } from 'component/Incentive/Voucher/CouponVoyage/__atoms/button_click.atom';
import { usePromotionText } from 'component/Incentive/Voucher/CouponVoyage/__hooks/usePromotionText';
import Cta from 'component/Layout/Cta';
import { CtaType } from 'component/Layout/Cta/types';
import NumberIcon from 'component/Layout/Icon/Number';

import classNames from 'classnames';
import { useAtom } from 'jotai';

import { QaIdentifier, useQa } from 'hooks/useQa';
import { useQueryEndpoint } from 'hooks/useQueryEndpoint';
import { useTranslation } from 'hooks/useTranslation';

import { ReactComponent as IconCase } from 'media/svg/case.svg';
import { ReactComponent as IconCheckmark } from 'media/svg/checkmark.svg';
import { ReactComponent as IconExclamationMarkCircle } from 'media/svg/exclamationMarkCircle.svg';

import ErrorBoundary from 'utility/Debug/ErrorBoundary';

import { ApiEndpoint } from 'types/Request/endpoint';

import { ResponseStatus } from 'proto/Api/ResponseStatus';

import {
    PROMOTIONAL_VOUCHER_CONTAINER,
    PORTAL_PROMOTIONAL_VOUCHER_BANNER_SKELETON,
} from '../__config/constants';
import { removeCsCodeFromUrl } from '../__helper';

import styles from './Banner.module.scss';
interface BannerProps {
    customClassList?: {
        Subline?: string;
        Headline?: string;
        Tagline?: string;
        CtaWrapper?: string;
        Banner?: string;
    };
    onFetch?: () => void;
}
const Banner = ({ customClassList, onFetch }: BannerProps) => {
    document
        .getElementById(PORTAL_PROMOTIONAL_VOUCHER_BANNER_SKELETON)
        ?.remove();
    const container = document.getElementById(PROMOTIONAL_VOUCHER_CONTAINER);
    const qaAttrBanner = useQa(QaIdentifier.CouponVoyage_Banner);
    const { t } = useTranslation();
    const getPromotionText = usePromotionText();
    const [buttonClicked, setButtonClicked] = useAtom(
        couponVoyageButtonClickAtom
    );
    const promotionalData = container ? JSON.parse(container.innerHTML) : null;
    const isActivated = promotionalData?.activated || false;
    const countdown = promotionalData?.countdown;
    const promotionUntil = promotionalData?.promotionUntil;
    const showWhileSuppliesLast =
        countdown === null || (countdown < 0 && promotionUntil === null);

    const { isFetching, refetch, error, data } = useQueryEndpoint(
        ApiEndpoint.ActivateVoucher,
        {},
        {
            enabled: false,
        }
    );
    const isError = data?.status === ResponseStatus.Error || !!error;

    useEffect(() => {
        if (buttonClicked) {
            refetch().then((result) => {
                if (
                    !result.isError ||
                    result.data?.status !== ResponseStatus.Error
                ) {
                    removeCsCodeFromUrl();
                    onFetch?.();
                    setButtonClicked(false);
                }
            });
        }
    }, [buttonClicked, onFetch, refetch, setButtonClicked]);

    const onClick = useCallback(async () => {
        const result = await refetch();
        if (!result.isError || result.data?.status !== ResponseStatus.Error) {
            removeCsCodeFromUrl();
            setTimeout(() => {
                onFetch?.();
            }, 1000);
        }
    }, [onFetch, refetch]);

    const isActive = isActivated || data?.status === ResponseStatus.Success;

    const subline = isActivated
        ? `${t('TRANS-your-personal-cashback-was-added-book-trip-get-reward')}`
        : `${t('TRANS-activate-your-cashback-voucher-and-save-banner')}${
              showWhileSuppliesLast ? ` ${t('TRANS-while-supplies-last')}.` : ''
          }`;

    if (!promotionalData) {
        return null;
    }

    return (
        <ErrorBoundary>
            <div
                className={classNames(styles.Banner, customClassList?.Banner)}
                {...qaAttrBanner}
            >
                <div className={styles.Image}>
                    <div className={styles.NumberIcon}>
                        <NumberIcon value={promotionalData.maxDiscount} />
                    </div>
                    <IconCase />
                </div>
                <div className={styles.TextContent}>
                    <p
                        className={classNames(
                            styles.Tagline,
                            customClassList?.Tagline
                        )}
                    >
                        {getPromotionText(countdown, promotionUntil)}
                    </p>
                    <h1
                        className={classNames(
                            styles.Headline,
                            customClassList?.Headline
                        )}
                    >
                        {t('TRANS-secure-up-to-x-euro-cashback', {
                            amount: promotionalData.maxDiscount,
                        })}
                    </h1>
                    <p
                        className={classNames(
                            styles.Subline,
                            customClassList?.Subline
                        )}
                    >
                        <span>{subline}</span>
                    </p>
                </div>
                <div
                    className={classNames(
                        customClassList?.CtaWrapper,
                        styles.CtaWrapper
                    )}
                >
                    <Cta
                        type={CtaType.BUTTON}
                        withLoader={isFetching}
                        onClick={onClick}
                        customClassList={{
                            Cta: isActive ? styles.ActiveCta : styles.Cta,
                            Spinner: isFetching ? styles.Loader : '',
                        }}
                    >
                        <span>
                            {isActive
                                ? t('TRANS-cashback-activated')
                                : t('TRANS-activate-cashback')}
                            {isActive && !isFetching && <IconCheckmark />}
                        </span>
                    </Cta>
                    {isError && (
                        <div className={styles.ErrorMessage}>
                            <IconExclamationMarkCircle />
                            <span>
                                {t('TRANS-an-error-occurred-please-try-again')}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </ErrorBoundary>
    );
};

export default Banner;
